import { getTencentCaptchaKey, verifyCaptcha } from '@/services/user';
import { Message } from '@bluevision/components';
import { useTranslation } from 'react-i18next';
import { useCallback, useRef } from 'react';
import { captchaType } from '@/components/VerifyCodeModal';
import { ResponseStructure } from '@/utils/request';
import Track from '@/tracks';
/** APP_ID */
const CAPTCHA_APP_ID = '191573373';
// 请求成功code
const successCode = '000000';
// 验证码错误常量
const CAPTCHA_ERROR_CODE = 1001;
const CAPTCHA_ERROR_MESSAGE = 'jsload_error';

// 生成随机字符串
const generateRandomString = () => '@' + Math.random().toString(36).substr(2);

// 生成错误票据
const generateErrorTicket = (timestamp: number) =>
  `trerror_${CAPTCHA_ERROR_CODE}_${CAPTCHA_APP_ID}_${timestamp}`;

interface ICaptchaResult {
  ret: number;
  ticket: string;
  randstr: string;
  CaptchaAppId?: string;
  bizState?: string;
  errorCode?: number;
  errorMessage?: string;
}

type VerifyCaptchaFunc = (data: {
  ticket: string;
  randstr: string;
}) => Promise<ResponseStructure<unknown>>;

interface captchaInitType {
  successCb?: (captcha: captchaType) => Promise<void> | void; // 滑动成功回调
  loadSuccessCb?: () => void | Promise<void>; // 加载成功回调
  verifyCaptchaFunc?: VerifyCaptchaFunc; // 验证验证码
  userEmail?: string; // 用户邮箱
}

export default function useCaptcha() {
  const { t, i18n } = useTranslation();

  // 使用 useRef 来存储回调函数
  const successCbRef = useRef<(captcha: captchaType) => void | Promise<void>>();
  const loadSuccessCbRef = useRef<() => void | Promise<void>>();
  const verifyCbRef = useRef<VerifyCaptchaFunc>();
  const userEmailRef = useRef<string>();

  // 通用错误上报函数
  const reportError = useCallback(
    (
      eventName: string,
      error: unknown,
      extraData?: Record<string, unknown>,
    ) => {
      Track.track(eventName, {
        captchaAppId: CAPTCHA_APP_ID,
        userEmail: userEmailRef.current || '未获取',
        userAgent: navigator.userAgent,
        language: i18n.language,
        error,
        ...extraData,
      });
    },
    [i18n.language],
  );

  // 滑块回调
  const captchaCallback = useCallback(
    async (res: ICaptchaResult) => {
      const { ticket, randstr, errorCode, errorMessage } = res;

      // 处理错误情况
      if (errorCode || errorMessage) {
        reportError('滑块验证码验证失败', errorMessage || 'Unknown error', {
          errorCode,
          ticket,
          randstr,
        });
        return;
      }

      if (ticket && randstr) {
        try {
          const response = verifyCbRef.current
            ? await verifyCbRef.current({ ticket, randstr })
            : await verifyCaptcha({ ticket, randstr });

          if (response.code === successCode) {
            successCbRef.current?.({ ticket, randstr }); // 调用存储的回调函数
          } else {
            Message.error(response.info);
            reportError('滑块验证码后端验证失败', response.info, {
              code: response.code,
              ticket,
              randstr,
            });
          }
        } catch (error) {
          const errorMsg = t('failed_to_obtain_the_verification_code');
          Message.error(errorMsg);
          reportError('滑块验证码网络请求失败', error, {
            ticket,
            randstr,
          });
        }
      }
    },
    [t, reportError],
  );
  // 加载失败
  const captchaLoadErrorCallback = useCallback(() => {
    const timestamp = Math.floor(Date.now() / 1000);
    const ticket = generateErrorTicket(timestamp);
    const randstr = generateRandomString();

    const errorInfo = {
      ret: 0,
      randstr,
      ticket,
      errorCode: CAPTCHA_ERROR_CODE,
      errorMessage: CAPTCHA_ERROR_MESSAGE,
    };

    // 调用验证码回调
    captchaCallback(errorInfo);

    // 上报错误埋点
    reportError('滑块验证码加载失败', CAPTCHA_ERROR_MESSAGE, {
      errorCode: CAPTCHA_ERROR_CODE,
      ticket,
    });
  }, [captchaCallback, reportError]);

  // 开启滑块验证
  const onCaptchaShow = useCallback(
    (encryptData: string) => {
      try {
        // @ts-expect-error TencentCaptcha无法被识别
        const captcha = new TencentCaptcha(CAPTCHA_APP_ID, captchaCallback, {
          aidEncrypted: encryptData,
          userLanguage: i18n.language,
        });
        captcha.show();
        loadSuccessCbRef.current?.(); // 调用存储的回调函数
      } catch (error) {
        reportError('滑块验证码初始化失败', error);
        captchaLoadErrorCallback();
      }
    },
    [captchaLoadErrorCallback, captchaCallback, reportError, i18n.language],
  );

  const captchaInit = async ({
    successCb,
    loadSuccessCb,
    verifyCaptchaFunc,
    userEmail,
  }: captchaInitType) => {
    successCbRef.current = successCb; // 存储回调函数
    loadSuccessCbRef.current = loadSuccessCb; // 存储加载完成回调函数
    verifyCbRef.current = verifyCaptchaFunc;
    userEmailRef.current = userEmail; // 存储用户邮箱

    const res = await getTencentCaptchaKey();

    if (res.code === successCode) {
      onCaptchaShow(res.data.encrypted_key);
    } else {
      // 如果有错误信息，提示错误信息
      Message.error(res.info);
    }
  };

  return captchaInit;
}
